import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { DOMAIN_CONFIG } from '@/integrations/supabase/config';
import AuthLayout from '@/components/auth/AuthLayout';

interface InvitationData {
  email: string;
  role: string;
  invited_by: string;
  expires_at: string;
}

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'invalid' | 'processing' | 'password_setup' | 'success'>('loading');
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [userProfile, setUserProfile] = useState<{ is_recruiter: boolean; is_superadmin: boolean } | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    verifyAndAcceptInvitation();
  }, []);

  const verifyAndAcceptInvitation = async () => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('invalid');
      setError('No invitation token provided');
      return;
    }

    try {
      const { data: invitationData, error: invitationError } = await supabase
        .from('user_invitations')
        .select('email, role, invited_by, expires_at, status')
        .eq('token', token)
        .single();

      if (invitationError || !invitationData) {
        setStatus('invalid');
        setError('Invalid invitation token');
        return;
      }

      if (invitationData.status !== 'pending') {
        setStatus('invalid');
        setError('This invitation has already been used or cancelled');
        return;
      }

      if (new Date(invitationData.expires_at) < new Date()) {
        setStatus('invalid');
        setError('This invitation has expired');
        return;
      }

      setInvitation(invitationData);
      setStatus('processing');

      const { data, error } = await supabase.functions.invoke('accept-invitation', {
        body: { token },
      });

      if (error || !data?.success) {
        let errorMessage = 'Failed to accept invitation';
        if (error?.context instanceof Response) {
          try {
            const errorBody = await error.context.json();
            errorMessage = errorBody?.message || errorMessage;
          } catch {
            // Could not parse error body
          }
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (error?.message && !error.message.includes('non-2xx')) {
          errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.temp_password,
      });

      if (signInError) {
        throw new Error('Failed to establish session');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Failed to get user after sign in');
      }

      setUserId(user.id);

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_recruiter, is_superadmin')
        .eq('id', user.id)
        .single();

      setUserProfile(profile);
      setStatus('password_setup');

    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setStatus('invalid');
      setError(err.message || 'Failed to accept invitation');
    }
  };

  const redirectBasedOnRole = () => {
    setTimeout(() => {
      if (userProfile?.is_recruiter) {
        window.location.href = `https://${DOMAIN_CONFIG.recruiterDomain}/search`;
      } else if (userProfile?.is_superadmin) {
        navigate('/superadmin');
      } else {
        navigate('/dashboard/overview');
      }
    }, 1500);
  };

  const handleSetPassword = async () => {
    setFormError('');

    if (!firstName.trim()) {
      setFormError('First name is required');
      return;
    }
    if (!lastName.trim()) {
      setFormError('Last name is required');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (!consentChecked) {
      setFormError('You must agree to the Privacy Policy and Terms of Service');
      return;
    }

    setSettingPassword(true);
    try {
      const { error: passwordError } = await supabase.auth.updateUser({
        password,
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          name: `${firstName.trim()} ${lastName.trim()}`,
        },
      });
      if (passwordError) throw passwordError;

      // Save first and last name to profile and users table
      if (userId) {
        const displayName = `${firstName.trim()} ${lastName.trim()}`;
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            display_name: displayName,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (profileError) {
          console.error('Error updating profile name:', profileError);
        }

        // Update users table (used by wcd-hiring) with first/last name
        await supabase
          .from('users')
          .update({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
          })
          .eq('id', userId);

        // Log GDPR consent with active policy versions
        const { data: activePolicies } = await supabase
          .from('policy_versions')
          .select('policy_type, version')
          .eq('status', 'active')
          .in('policy_type', ['privacy_policy', 'terms_acceptance']);

        const policyVersionMap: Record<string, string> = {};
        activePolicies?.forEach((p) => {
          policyVersionMap[p.policy_type] = p.version;
        });

        const consentBase = {
          user_id: userId,
          email: invitation?.email || '',
          granted: true,
          source_form: 'invitation_signup',
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        };

        await supabase.from('consent_logs').insert([
          { ...consentBase, consent_type: 'privacy_policy' as const, policy_version: policyVersionMap['privacy_policy'] || '1.0' },
          { ...consentBase, consent_type: 'terms_acceptance' as const, policy_version: policyVersionMap['terms_acceptance'] || '1.0' },
        ]);
      }

      setStatus('success');
      redirectBasedOnRole();
    } catch (err: any) {
      setFormError(err.message || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSetPassword();
  };

  if (status === 'loading' || status === 'processing') {
    return (
      <AuthLayout>
        <Card className="w-full max-w-sm mx-auto border-0 shadow-sm">
          <CardHeader className="text-center pt-10 pb-6">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <CardTitle className="text-[17px] font-normal font-prociono tracking-wide">
              {status === 'loading' ? 'Verifying invitation...' : 'Setting up your account...'}
            </CardTitle>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  if (status === 'invalid') {
    return (
      <AuthLayout>
        <Card className="w-full max-w-sm mx-auto border-0 shadow-sm">
          <CardHeader className="text-center pt-10 pb-6">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-[17px] font-normal font-prociono tracking-wide">
              Invalid invitation
            </CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardContent className="pb-10">
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-[#2c1810] hover:bg-[#1a0f09] text-white font-normal"
            >
              Back to login
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (status === 'password_setup') {
    return (
      <AuthLayout>
        <Card className="w-full max-w-sm mx-auto border-0 shadow-sm">
          <CardHeader className="text-center pt-10 pb-6">
            <CardTitle className="text-[17px] font-normal font-prociono tracking-wide">
              Create your account
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-10">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">First name</label>
                  <Input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Last name</label>
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <Input
                  type="password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm password</label>
                <Input
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <a href="/privacy" target="_blank" className="underline text-foreground hover:text-primary">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/terms" target="_blank" className="underline text-foreground hover:text-primary">
                    Terms of Service
                  </a>
                </span>
              </label>
              {formError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {formError}
                </p>
              )}
              <Button
                onClick={handleSetPassword}
                disabled={settingPassword || !consentChecked}
                className="w-full bg-[#2c1810] hover:bg-[#1a0f09] text-white font-normal"
              >
                {settingPassword ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <AuthLayout>
        <Card className="w-full max-w-sm mx-auto border-0 shadow-sm">
          <CardHeader className="text-center pt-10 pb-6">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-[17px] font-normal font-prociono tracking-wide">
              Welcome aboard!
            </CardTitle>
            <CardDescription>Redirecting to your dashboard...</CardDescription>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  return null;
};

export default AcceptInvitation;
